
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Star, AlertTriangle, Users, Target, Award, Crown } from "lucide-react";
import { PerformanceInsights } from "../types";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface PerformanceTrajectoriesTabProps {
  insights: PerformanceInsights;
}

const PerformanceTrajectoriesTab = ({ insights }: PerformanceTrajectoriesTabProps) => {
  // Get real counts from the insights data with proper null checks
  const totalHighPerformers = insights.performanceTrajectories?.highPerformers?.length || 0;
  const totalEmergingTalent = insights.performanceTrajectories?.emergingTalent?.length || 0;
  const totalAveragePerformers = insights.performanceTrajectories?.averagePerformers?.length || 0;
  const totalDeclineAlerts = insights.performanceTrajectories?.declineAlerts?.length || 0;
  
  // Performance distribution for pie chart with enhanced colors
  const performanceDistribution = [
    { name: 'Band A (High Performers)', value: totalHighPerformers, text: 'white', color: '#10b981', gradient: 'from-emerald-500 to-green-600' },
    { name: 'Band B (Emerging Talent)', value: totalEmergingTalent, text: 'white', color: '#3b82f6', gradient: 'from-blue-500 to-indigo-600' },
    { name: 'Band C (Average)', value: totalAveragePerformers, text: 'white', color: '#f59e0b', gradient: 'from-amber-500 to-orange-600' },
    { name: 'Band D (At Risk)', value: totalDeclineAlerts, text: 'white', color: '#ef4444', gradient: 'from-red-500 to-rose-600' },
  ].filter(item => item.value > 0);

  // Band distribution for bar chart with enhanced styling
  const bandDistribution = [
    { band: 'Band A', count: totalHighPerformers, color: '#10b981', gradient: 'from-emerald-400 to-emerald-600' },
    { band: 'Band B', count: totalEmergingTalent, color: '#3b82f6', gradient: 'from-blue-400 to-blue-600' },
    { band: 'Band C', count: totalAveragePerformers, color: '#f59e0b', gradient: 'from-amber-400 to-amber-600' },
    { band: 'Band D', count: totalDeclineAlerts, color: '#ef4444', gradient: 'from-red-400 to-red-600' },
  ];

  // Custom label for pie chart
  const renderCustomizedPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-sm font-bold drop-shadow-lg"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-md border border-primary/30 p-4 rounded-lg shadow-2xl">
          <p className="text-white font-semibold text-sm mb-1">{payload[0].name || label}</p>
          <p className="text-primary font-bold text-lg">{`Count: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  // Custom pie tooltip
  const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-md border border-primary/30 p-4 rounded-lg shadow-2xl">
          <p className="text-white font-semibold text-sm mb-1">{payload[0].name}</p>
          <p className="text-primary font-bold text-lg">{`Count: ${payload[0].value}`}</p>
          <p className="text-gray-300 text-xs">{`${((payload[0].value / (totalHighPerformers + totalEmergingTalent + totalAveragePerformers + totalDeclineAlerts)) * 100).toFixed(1)}% of team`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Performance Overview Metrics - Now 6 cards in one row */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-300/80">Total Employees</p>
                <p className="text-3xl font-bold text-blue-400 animate-pulse">{insights.executiveSummary.totalEmployees}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/10 border-green-500/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-green-300/80">High Performers</p>
                <p className="text-3xl font-bold text-green-400 animate-pulse">{totalHighPerformers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-300/80">Emerging Talent</p>
                <p className="text-3xl font-bold text-blue-400 animate-pulse">{totalEmergingTalent}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-300/80">Average Performers</p>
                <p className="text-3xl font-bold text-amber-400 animate-pulse">{totalAveragePerformers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/10 border-red-500/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-red-300/80">Low Performers</p>
                <p className="text-3xl font-bold text-red-400 animate-pulse">{totalDeclineAlerts}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-300/80">Average Unit Performance</p>
                <p className="text-3xl font-bold text-amber-400 animate-pulse">{insights.executiveSummary.averagePerformanceRating.toFixed(1)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Performance Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-secondary/20 to-secondary/5 border-primary/20 shadow-xl hover:shadow-2xl transition-all duration-300 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-xl">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Target className="h-6 w-6 text-primary" />
              </div>
              Current Performance Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 relative">
              {performanceDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {performanceDistribution.map((entry, index) => (
                        <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor={entry.color} stopOpacity={0.8} />
                          <stop offset="100%" stopColor={entry.color} stopOpacity={1} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={performanceDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedPieLabel}
                      outerRadius={120}
                      innerRadius={40}
                      fill="#8884d8"
                      dataKey="value"
                      animationBegin={0}
                      animationDuration={1500}
                    >
                      {performanceDistribution.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={`url(#gradient-${index})`}
                          stroke="#ffffff"
                          strokeWidth={2}
                          className="hover:opacity-80 transition-opacity duration-200"
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      wrapperStyle={{ paddingTop: '20px', fontSize: '12px', color: 'white' }}
                      iconType="circle"
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-400">
                    <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg mb-2">No Performance Data Available</p>
                    <p className="text-sm">Complete performance reviews to see distribution</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-secondary/20 to-secondary/5 border-primary/20 shadow-xl hover:shadow-2xl transition-all duration-300 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-xl">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Award className="h-6 w-6 text-primary" />
              </div>
              Performance Band Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={bandDistribution} 
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <defs>
                    {bandDistribution.map((entry, index) => (
                      <linearGradient key={`bar-gradient-${index}`} id={`bar-gradient-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={entry.color} stopOpacity={0.9} />
                        <stop offset="100%" stopColor={entry.color} stopOpacity={1} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis 
                    dataKey="band" 
                    stroke="#9ca3af" 
                    fontSize={12}
                    fontWeight={500}
                  />
                  <YAxis 
                    stroke="#9ca3af" 
                    fontSize={12}
                    fontWeight={500}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    radius={[8, 8, 0, 0]}
                    animationDuration={1500}
                    animationBegin={200}
                  >
                    {bandDistribution.map((entry, index) => (
                      <Cell 
                        key={`bar-cell-${index}`} 
                        fill={`url(#bar-gradient-${index})`}
                        stroke={entry.color}
                        strokeWidth={1}
                        className="hover:opacity-80 transition-opacity duration-200"
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Performance Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/10 border-green-500/30 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              High Performers ({totalHighPerformers})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {insights.performanceTrajectories?.highPerformers && insights.performanceTrajectories.highPerformers.length > 0 ? (
              insights.performanceTrajectories.highPerformers.map((performer, index) => (
                <div key={index} className="border-l-4 border-green-400 pl-4 bg-green-900/20 p-3 rounded-r hover:bg-green-900/30 transition-colors duration-200">
                  <h4 className="font-semibold text-white mb-1">{performer.employeeName}</h4>
                  <p className="text-sm text-gray-300 mb-2">{performer.trend}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-green-800/30 text-green-300 px-2 py-1 rounded">
                      Band A - Top Performer
                    </span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-white/70">Recommendations:</p>
                    {performer.recommendations?.map((rec, idx) => (
                      <p key={idx} className="text-xs text-green-300">• {rec}</p>
                    )) || []}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-400 py-4">
                <Crown className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No Band A performers identified</p>
                <p className="text-xs mt-2">Complete performance reviews to identify top talent</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/30 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              Emerging Talent ({totalEmergingTalent})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {insights.performanceTrajectories?.emergingTalent && insights.performanceTrajectories.emergingTalent.length > 0 ? (
              insights.performanceTrajectories.emergingTalent.map((talent, index) => (
                <div key={index} className="border-l-4 border-blue-400 pl-4 bg-blue-900/20 p-3 rounded-r hover:bg-blue-900/30 transition-colors duration-200">
                  <h4 className="font-semibold text-white mb-1">{talent.employeeName}</h4>
                  <p className="text-sm text-gray-300 mb-2">{talent.growthTrajectory}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-blue-800/30 text-blue-300 px-2 py-1 rounded">
                      Band B - Rising Star
                    </span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-white/70">Development Areas:</p>
                    {talent.potentialAreas?.map((area, idx) => (
                      <p key={idx} className="text-xs text-blue-300">• {area}</p>
                    )) || []}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-400 py-4">
                <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No Band B performers identified</p>
                <p className="text-xs mt-2">Review performance trends to identify rising stars</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/10 border-red-500/30 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              Performance Alerts ({totalDeclineAlerts})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {insights.performanceTrajectories?.declineAlerts && insights.performanceTrajectories.declineAlerts.length > 0 ? (
              insights.performanceTrajectories.declineAlerts.map((alert, index) => (
                <div key={index} className="border-l-4 border-red-400 pl-4 bg-red-900/20 p-3 rounded-r hover:bg-red-900/30 transition-colors duration-200">
                  <h4 className="font-semibold text-white mb-1">{alert.employeeName}</h4>
                  <p className="text-sm text-gray-300 mb-2">{alert.declineTrend}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-red-800/30 text-red-300 px-2 py-1 rounded">
                      Band D - Urgent Action
                    </span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-white/70">Interventions Needed:</p>
                    {alert.interventionNeeded?.map((intervention, idx) => (
                      <p key={idx} className="text-xs text-red-300">• {intervention}</p>
                    )) || []}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-400 py-4">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50 text-green-400" />
                <p className="text-sm text-green-400">No Band D performers identified</p>
                <p className="text-xs mt-2">This indicates good overall team performance</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerformanceTrajectoriesTab;
